import { Box, FlexContainer, ReemaThemeV2 } from '@act/storybook';
import { Box as MuiBox, Slide } from '@mui/material';
import { forwardRef, useEffect, useState } from 'react';

export type GoalsSidePanelProps = {
  titleContent: React.ReactNode;
  content: React.ReactNode;
  footerContent: React.ReactNode;
  open: boolean;
  animated?: boolean;
  setShouldRenderSidePanel?: (shouldRender: boolean) => void;
  /** Fixed width for form drawers (e.g. 530); defaults to 99% of the parent column. */
  panelWidth?: string | number;
};

type SidePanelContentProps = {
  titleContent: React.ReactNode;
  content: React.ReactNode;
  footerContent: React.ReactNode;
  panelWidth?: string | number;
};

const SidePanelContent = forwardRef<HTMLDivElement, SidePanelContentProps>(
  ({ titleContent, content, footerContent, panelWidth }, ref) => {
    const resolvedWidth =
      panelWidth === undefined
        ? '99%'
        : typeof panelWidth === 'number'
          ? `${panelWidth}px`
          : panelWidth;

    const panel = (
      <MuiBox
        ref={ref}
        display="flex"
        flexDirection="column"
        height="100%"
        width={resolvedWidth}
        maxWidth="100%"
        position="relative"
      >
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          minHeight={0}
          height="100%"
          width="100%"
          backgroundColor="surfaces.neutralLighter"
          boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
          marginLeft={panelWidth === undefined ? '8px' : '0'}
          borderRadius={panelWidth === undefined ? '12px' : 0}
          backdropFilter="blur(8px)"
          border="1px solid rgba(255, 255, 255, 0.2)"
        >
          <FlexContainer
            direction="column"
            flexGrow={1}
            flexBasis="0"
            minHeight="0"
            height="100%"
            padding="24px"
            width="100%"
            overflow="hidden"
            overflowX="hidden"
            overflowY="hidden"
            gap={2}
          >
            <Box flexShrink={0}>{titleContent}</Box>
            <FlexContainer
              direction="column"
              flexGrow={1}
              flexBasis="0"
              minHeight="0"
              gap={3}
              width="100%"
              overflowY="auto"
              overflowX="hidden"
              height="auto"
            >
              {content}
            </FlexContainer>
          </FlexContainer>
          <Box
            padding="16px 24px"
            borderTop={`1px solid ${ReemaThemeV2.LIGHT_MODE.palette.greyShades[250]}`}
            flexShrink={0}
            backgroundColor="surfaces.neutralLighter"
            width="95%"
            display="flex"
            justifyContent="flex-end"
          >
            {footerContent}
          </Box>
        </Box>
      </MuiBox>
    );

    if (panelWidth === undefined) {
      return panel;
    }

    return (
      <FlexContainer
        direction="row"
        height="100%"
        width="100%"
        justifyContent="flex-end"
        overflowAllVisible
      >
        {panel}
      </FlexContainer>
    );
  }
);

export const GoalsSidePanel = ({
  titleContent,
  content,
  footerContent,
  open,
  setShouldRenderSidePanel,
  animated = false,
  panelWidth,
}: GoalsSidePanelProps) => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (animated && open) {
      setShouldRender(true);
    }
  }, [open, animated]);

  if (animated) {
    const handleExited = () => {
      setShouldRender(false);
      setShouldRenderSidePanel?.(false);
    };

    if (!shouldRender) return null;

    return (
      <Slide
        direction="left"
        in={open}
        timeout={225}
        onExited={handleExited}
        mountOnEnter
        unmountOnExit={false}
      >
        <SidePanelContent
          titleContent={titleContent}
          content={content}
          footerContent={footerContent}
          panelWidth={panelWidth}
        />
      </Slide>
    );
  } else {
    if (!open) return null;
    return (
      <SidePanelContent
        titleContent={titleContent}
        content={content}
        footerContent={footerContent}
        panelWidth={panelWidth}
      />
    );
  }
};
